import React, { useMemo } from 'react';
import LastSaved from '../LastSaved';
import { Badge } from '@GxT-Team/gxt-components';
import useClientInfo from '../../hooks/useClientInfo';
import UserAvatarGroup from '../elements/UserAvatarGroup';
import { ActiveUser } from '../../types';

type InfoPanelProps = {
  lastSaved?: Date;
  badges?: {
    label: string;
    values: string[];
  }[];
  activeUsers?: ActiveUser[];
};

const InfoPanel = ({ lastSaved, badges = [], activeUsers = [] }: InfoPanelProps) => {
  const { userId: currentUserId } = useClientInfo();
  // Get unique other users who are currently editing using Map for efficiency
  const otherActiveUsers = useMemo(() => {
    const userMap: Map<string, { id: string; name: string }> = new Map();

    Object.values(activeUsers).forEach((node) => {
      const user = node as ActiveUser;
      if (user.userId !== currentUserId && !userMap.has(user.userId)) {
        userMap.set(user.userId, { id: user.userId, name: user.userName });
      }
    });

    return Array.from(userMap.values());
  }, [currentUserId, activeUsers]);

  return (
    <div className="mb-5 flex justify-start items-center space-x-6">
      {lastSaved && <LastSaved timestamp={lastSaved} />}
      <div className="flex justify-between flex-1">
        {badges.reduce((acc, item) => {
          if (item.values.length) {
            acc.push(
              <div className="flex gap-2 items-center" key={item.label}>
                <h5 className="text-neutral-400">{item.label}</h5>
                {item.values.map((value) => (
                  <Badge key={value} label={value} type="nosave" />
                ))}

                {item.label === 'Status' && otherActiveUsers.length > 0 && (
                  <div className="flex items-center ml-2">
                    <UserAvatarGroup users={otherActiveUsers} maxVisible={3} />
                  </div>
                )}
              </div>,
            );
          }
          return acc;
        }, [] as React.ReactNode[])}
      </div>
    </div>
  );
};

export default InfoPanel;
